import React, { useEffect, useRef, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * Pull-to-refresh wrapper — only active when the app is running as an
 * installed PWA (standalone display mode). In a normal browser tab the
 * built-in pull-to-refresh works, so we don't hijack it there.
 *
 * Guardrails to prevent accidental refreshes:
 *  - Only fires when the page is already scrolled to the very top
 *  - Only starts if the initial touch is in the upper portion of the screen
 *  - Ignored when a Radix dialog / sheet (menu drawer, modal) is open
 *  - Ignored when the touch begins inside an input, textarea, select or button
 *    (so tapping / focusing the search bar never triggers a pull)
 */
const PULL_THRESHOLD = 110;      // px pulled before a refresh triggers (was 70 — less sensitive)
const MAX_PULL = 150;            // cap so the indicator doesn't fly off
const START_ZONE_TOP_PX = 220;   // touch must begin within top 220px of viewport
const RESISTANCE = 0.4;          // dampen the pull distance (was 0.5)

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  // iOS Safari
  if (window.navigator.standalone === true) return true;
  // Android / desktop PWA
  return window.matchMedia('(display-mode: standalone)').matches;
};

// True when any menu drawer / modal / dialog is open on the page.
const isModalOpen = () => {
  if (typeof document === 'undefined') return false;
  if (document.body.hasAttribute('data-scroll-locked')) return true;
  // Radix marks open dialogs and sheets with data-state="open"
  return !!document.querySelector('[role="dialog"][data-state="open"], [data-state="open"][data-side]');
};

// True when the initial touch target is an interactive element we should NOT hijack.
const isInteractiveTarget = (el) => {
  if (!el) return false;
  const t = el.tagName;
  if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A', 'LABEL'].includes(t)) return true;
  if (el.isContentEditable) return true;
  // Anything nested inside an input / search / button
  if (el.closest && el.closest('input, textarea, select, button, [role="button"], [data-no-refresh]')) return true;
  return false;
};

const PullToRefresh = ({ children }) => {
  const [enabled, setEnabled] = useState(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(null);
  const activeRef = useRef(false);

  useEffect(() => {
    setEnabled(isStandalone());
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (!enabled || refreshing) return;
    // Do nothing while a modal / menu drawer / sheet is open
    if (isModalOpen()) return;
    // Only start when the page is scrolled to the very top
    if ((window.scrollY || document.documentElement.scrollTop) > 0) return;
    // Ignore touches on interactive elements (inputs, buttons, search bar…)
    if (isInteractiveTarget(e.target)) return;
    // Only start pulls that begin in the top portion of the viewport
    const startY = e.touches[0].clientY;
    if (startY > START_ZONE_TOP_PX) return;
    startYRef.current = startY;
    activeRef.current = true;
  }, [enabled, refreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!activeRef.current || refreshing) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startYRef.current;
    if (diff <= 0) {
      setPull(0);
      return;
    }
    // Block the native iOS rubber-band overscroll so the sticky header
    // stays put and only our custom indicator moves.
    if (e.cancelable) {
      e.preventDefault();
    }
    // Apply resistance and cap
    const resisted = Math.min(MAX_PULL, diff * RESISTANCE);
    setPull(resisted);
  }, [refreshing]);

  const handleTouchEnd = useCallback(() => {
    if (!activeRef.current) return;
    activeRef.current = false;
    if (pull >= PULL_THRESHOLD && !refreshing) {
      setRefreshing(true);
      setPull(PULL_THRESHOLD);
      // Give the user a beat of visual feedback then reload
      setTimeout(() => {
        window.location.reload();
      }, 350);
    } else {
      setPull(0);
    }
  }, [pull, refreshing]);

  useEffect(() => {
    if (!enabled) return undefined;
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    // touchmove is NOT passive so we can preventDefault to suppress the
    // native rubber-band overscroll while a pull is active.
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  if (!enabled) return children;

  const progress = Math.min(1, pull / PULL_THRESHOLD);
  const opacity = Math.min(1, pull / 40);
  const rotation = progress * 270; // rotate arrow as user pulls

  return (
    <>
      {/* Indicator — fixed near the top edge, comes down as user pulls */}
      <div
        aria-hidden={pull === 0 && !refreshing}
        style={{
          position: 'fixed',
          top: 0,
          left: '50%',
          transform: `translate(-50%, ${Math.max(0, pull - 40)}px)`,
          transition: activeRef.current ? 'none' : 'transform 220ms ease-out',
          opacity,
          zIndex: 9999,
          pointerEvents: 'none',
        }}
        data-testid="pull-to-refresh-indicator"
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 9999,
            background: '#FFD12D',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
          }}
        >
          <RefreshCw
            size={20}
            color="#252525"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: refreshing ? 'none' : 'transform 80ms linear',
              animation: refreshing ? 'ptr-spin 800ms linear infinite' : 'none',
            }}
          />
        </div>
      </div>
      <style>{`@keyframes ptr-spin{to{transform:rotate(360deg)}}`}</style>
      {children}
    </>
  );
};

export default PullToRefresh;
